import dayjs from "dayjs";
import { identity } from "lodash";
import styled from "styled-components";

import { ELandingPagesTabs } from "app/common/keyword-research/landing-pages/landingPagesTypes";
import {
    CellKeywordDashboard,
    DefaultCell,
    LinkCell,
    TrafficShareWithVisits,
    TrendCell,
} from "app/components/React/Table/cells";
import { ChangeStateCell } from "app/components/React/Table/cells/ChangeStateCell";
import { DefaultCellHeader } from "app/components/React/Table/headerCells";
import LandingPagesDataFetcherFactory from "app/components/widget/widget-fetchers/LandingPagesDataFetcherFactory";
import { TableLandingPageDashboardWidgetFilters } from "app/components/widget/widget-types/TableLandingPageDashboardWidgetFilters";
import { translate } from "app/filters/i18nFilter";
import {
    abbrNumberVisitsFilter,
    minVisitsAbbrFilter,
    percentageSignFilter,
    swNumberFilter,
} from "app/filters/ngFilters";
import { sourceTypeMap } from "app/common/keyword-research/landing-pages/landingPagesTypes";
import { DrilldownWrapper, StyledTrendContainer } from "app/components/keyword-research/landing-pages/styledComponents";
import { SHOW_PAID_LANDING_PAGE_AD_SPEND_COLUMN } from "app/pages/website-analysis/traffic-sources/search/common/showPaidLandingPageAdSpendColumn";
import { getUrlSpend } from "app/pages/website-analysis/traffic-sources/search/common/utils";
import { SerpTableCellSingle } from "app/pages/website-analysis/traffic-sources/search/components/serp/SerpTableCellSingle";
import { BasicDurations, apiFormat, customRangeFormat } from "app/services/DurationService";

import { TableTemplateV2 } from "../dashboard/TableTemplate-v2";
import { LandingPagesTableSubtitle } from "../widget-subtitle-templates/LandingPagesTableSubtitle";

import { EKWReportTableFilters, KWReportWidgetFiltersMap } from "./TableLandingPageDashboardConst";
import { TableWidget } from "./TableWidget";
import type { ISubtitleTextItem } from "./Widget";

export const WidgetFilter = styled.div`
    max-width: 280px;
    > div {
        white-space: break-spaces;
    }
`;

export class TableLandingPageDashboardWidget extends TableWidget {
    public getSearchKey() {
        return "SearchTerm";
    }

    runWidget() {
        if (this._autoFetchData) {
            this.getData();
        }
        this.setMetadata();
        this.getLegendImage();
    }

    public isPptSupported = () => {
        return false;
    };

    static getWidgetMetadataType() {
        return "TableLandingPageDashboard";
    }

    static getWidgetDashboardType() {
        return "Table";
    }

    static getWidgetResourceType() {
        return "Table";
    }

    protected getDataFetcherFactory() {
        return LandingPagesDataFetcherFactory;
    }

    static getFiltersComponent() {
        return TableLandingPageDashboardWidgetFilters;
    }

    get templateComponent() {
        return TableTemplateV2;
    }

    protected rowReducer(row) {
        const requestParams = this._params;
        const selectedMonth = dayjs.utc(requestParams.to, apiFormat).format(customRangeFormat);
        const is28Days = requestParams.latest === "28d";

        const stateParams = {
            isWWW: "*",
            duration: is28Days ? "28d" : `${selectedMonth}-${selectedMonth}`,
            country: requestParams.country,
            keyword: row.SearchTerm,
            webSource: requestParams.webSource,
        };

        return {
            ...row,
            url: this._swNavigator.href("organicsearch_keywordAnalysis_2", stateParams, {}),
        };
    }

    getSingleHeader = () => this.subtitleData;

    getSingleHeaderItem = () => this.subtitleData && this.subtitleData.TotalClicks;

    getAbbrNumberVisitsFilter = () => abbrNumberVisitsFilter();

    getMinVisitsAbbrFilter = () => minVisitsAbbrFilter();

    getPercentagesignFilter = () => percentageSignFilter();

    getColumnsConfig() {
        const { sourceType } = this.apiParams;
        const isPaidTab = sourceType === ELandingPagesTabs[ELandingPagesTabs.Paid].toLowerCase();

        const columns = [
            {
                name: "Url",
                title: translate("organic.landing.pages.table.columns.pageUrl.title"),
                cellComponent: LinkCell,
                cellClass: "url-cell",
                minWidth: "160px",
            },
            {
                name: "ClicksShare",
                title: translate("keywords.by.website.table.columns.clicks.title"),
                width: "170px",
                cellComponent: (props) => (
                    <TrafficShareWithVisits
                        {...props}
                        applyAbbrNumberFilter
                        totalVisitsRowProp="Clicks"
                        thresholdForTotalFilter={50}
                    />
                ),
            },
            {
                name: "ClicksChange",
                title: translate("organic.landing.pages.v2.table.columns.change.title"),
                cellTemp: "change-percentage",
                cellComponent: (props) => {
                    const { PrevClicks, Clicks, ClicksChange, ChangeState } = props.row;
                    const absChange = Math.abs(ClicksChange);
                    const precision = absChange > 0 && absChange < 0.1 ? 1 : 0;

                    return (
                        <ChangeStateCell
                            {...props}
                            value={{
                                first: PrevClicks,
                                last: Clicks,
                                change: ClicksChange,
                                changeState: ChangeState,
                            }}
                            undefinedSign={"-"}
                            isPercentage={true}
                            cellFilter={`simplePercentage:${precision}`}
                        />
                    );
                },
                width: 80,
            },
            {
                field: "Trend",
                width: 140,
                isResizable: true,
                title: translate("organic.landing.pages.v2.table.columns.clicks-trend.title"),
                cellComponent: (props) => {
                    return (
                        <DrilldownWrapper>
                            <TrendCell
                                {...props}
                                Container={StyledTrendContainer}
                            />
                        </DrilldownWrapper>
                    );
                },
                sortable: false,
            },
            isPaidTab &&
                SHOW_PAID_LANDING_PAGE_AD_SPEND_COLUMN && {
                    field: "UrlSpend",
                    title: translate("paid.landing.pages.table.columns.urlspend.title"),
                    cellComponent: ({ value }) => {
                        const urlSpend = getUrlSpend(value);
                        return <DefaultCell value={!urlSpend ? translate("common.undefined.text") : `$${urlSpend}`} />;
                    },
                    width: 120,
                    sortable: true,
                },
            {
                field: "KeywordsCount",
                title: translate("organic.landing.pages.table.columns.keywordsNum.title"),
                cellComponent: ({ value }) => {
                    return (
                        <DrilldownWrapper>
                            <DefaultCell value={swNumberFilter()(value)} />
                        </DrilldownWrapper>
                    );
                },
                width: 105,
                sortable: true,
            },
            {
                name: "SearchTerm",
                title: translate("organic.landing.pages.table.columns.topKeyword.title"),
                cellComponent: (props) => <CellKeywordDashboard {...props} />,
                width: 177,
            },
            isPaidTab && {
                field: "AdsCount",
                title: translate("organic.landing.pages.table.columns.ads.title"),
                cellComponent: ({ value }) => {
                    return (
                        <DrilldownWrapper>
                            <DefaultCell value={swNumberFilter()(value)} />
                        </DrilldownWrapper>
                    );
                },
                width: 105,
                sortable: true,
            },
            {
                field: "SerpFeatures",
                title: translate("serp.column.name"),
                headerComponent: DefaultCellHeader,
                cellComponent: SerpTableCellSingle,
                width: 140,
                sortable: false,
                isResizable: false,
            },
        ].filter(identity);

        return columns;
    }

    async getViewData() {
        super.getViewData();

        const webSource = this._widgetConfig.properties.webSource;
        if (!this._widgetConfig.properties.country && !webSource) {
            //in case devices was not selected yet
            return this.viewData;
        }
        this.viewData.subtitleTextItem = await this.getFiltersTooltip();
        this.viewData.customSubtitle = "true";
        this.viewData.customSubtitleComponent = LandingPagesTableSubtitle;
        return this.viewData;
    }

    async getFiltersTooltip(): Promise<ISubtitleTextItem> {
        const filtersToValidate = [
            EKWReportTableFilters.MULTI_INCLUDE_EXCLUDE_KEYWORDS,
            EKWReportTableFilters.MULTI_INCLUDE_EXCLUDE_WEBSITES,
            EKWReportTableFilters.CHANGE,
        ];
        const mappedFilters = await Promise.all(
            filtersToValidate.map((filter) => KWReportWidgetFiltersMap[filter](this._params)),
        );
        const availableFilters = mappedFilters.map((filter) => filter?.divElements);

        const calculateTotalDivCount = (accumulator, currentObject) => {
            if (currentObject && currentObject.divCount) {
                return accumulator + currentObject.divCount;
            }
            return accumulator;
        };

        const amountOfFilters = mappedFilters.reduce(calculateTotalDivCount, 0);

        if (!amountOfFilters) {
            return;
        }

        const filtersTextSubtitle =
            amountOfFilters > 1
                ? translate("dashboard.widget.subtitle.landing.page.filters.multiple", {
                      amount: amountOfFilters,
                  })
                : amountOfFilters === 1
                  ? translate("dashboard.widget.subtitle.landing.page.filters.unique")
                  : availableFilters[0];

        const tooltipValue = availableFilters.map((filter, index) => <WidgetFilter key={index}>{filter}</WidgetFilter>);
        return {
            text: filtersTextSubtitle,
            tooltipValue,
        };
    }

    public getProUrl(rowParams) {
        const {
            country,
            webSource,
            to,
            keys,
            latest,
            selectedDomain,
            pageFilter,
            Change,
            multiIncludeExcludeKeywords,
            multiIncludeExcludeWebsites,
            sourceType,
        } = this.apiParams;

        const is28Days = latest === BasicDurations.LAST_TWENTY_EIGHT_DAYS;
        const selectedMonth = dayjs.utc(to, apiFormat).format(customRangeFormat);
        const sourceKey = Object.keys(sourceTypeMap).find(
            (key) => sourceTypeMap[key as keyof typeof ELandingPagesTabs] === sourceType,
        );
        const toParams = {
            country,
            duration: is28Days ? BasicDurations.LAST_TWENTY_EIGHT_DAYS : `${selectedMonth}-${selectedMonth}`,
            webSource,
            key: keys,
            pageFilter,
            selectedDomain,
            Change,
            multiIncludeExcludeKeywords,
            multiIncludeExcludeWebsites,
            latest,
            selectedPageTab: sourceKey,
            ...(is28Days ? { latest } : {}),
        };
        return this._swNavigator.getStateUrl("organicsearch_website_landingpages_v2", {
            ...toParams,
        });
    }
}

TableLandingPageDashboardWidget.register();
